import { Grid2, Box, Button, TextField, InputAdornment, Typography, Collapse } from '@mui/material';
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from 'react-router-dom';
import AddIcon from "@mui/icons-material/Add";

import SPLotFormDialog from '../components/spLot/CreateSPLotsDialog';
import { useCommonStyles } from '../styles/GenericStyle';
import SPLotView from '../components/spLot/SpLotView';
import type { SPLotViewRef } from '../components/spLot/SpLotView';
import SPLotTab from '../components/spLot/SPLotTab';
import type { SPLotModel } from '../api/vegApl/SPLotModel';
import SearchIcon from '@mui/icons-material/Search';
import SPLotDataGrid from '../components/spLot/SPLotMainGrid';
import TrailDataGrid from '../components/trail/trailGrid';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useGlobalState } from "../contexts/GlobalState"
import { useSearchBoxSPLot, useGetLabelTemplates } from '../api/vegApl';
import type { LabelTemplate } from '../api/vegApl/labelTemplate';
import type { ListValueSpLot } from '../model/commonType';
import { toTitleCase,checkArray } from "../utils/HelperFunction";
import AddEditSPLotFormDialog from '../components/spLot/AddEditSPMatPopup';
import CreateLabelDialog from '../components/spLot/CreateLabelPopup';
import { MtComStyleObj } from '../styles/MtComStyleObj';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import barchartIcon from '/icons/barchart_icon.png';
import KPI from '../components/common/kpi';
import { usePersistedFilters } from '../hooks/usePersistedFilters';
import DashboardHeader from '../components/common/DashboardHeader';
import ReportNoSeedPopup from '../components/popup/reportnoseedpopup';
import { getSplotStatusIdsByTab, resolveAccessibleFilterByStatusIds } from '../utils/statusUtils';

export const SPLotPage = () => {
    const classes = useCommonStyles();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(() => {
        const savedTab = localStorage.getItem('spLotActiveTab');
        return savedTab ? parseInt(savedTab, 10) : 1;
    });

    // Honour ?activeTab= query param on mount
    useEffect(() => {
        const tabFromQuery = Number(searchParams.get('activeTab'));
        if (tabFromQuery === 1 || tabFromQuery === 2) {
            setActiveTab(tabFromQuery);
        }
    }, [searchParams]);
    const [openSPLotDialog, setOpenSPLotDialog] = useState(false);
    const [showMaterialLabelPopup, setShowMaterialLabelPopup] = useState(false);
    const [showKPI, setShowKPI] = useState(true);
    const [isFilterVisible, setFilterVisible] = useState(false);
    const [isGlobalSearchActive, setIsGlobalSearchActive] = useState(false);
    const [mode, setMode] = useState('view');
    const [drillDownData, setDrillDownParam] = useState<SPLotModel | null>(null);
    const [spLotGridData, setSpLotGridData] = useState<String[]>([])
    const [searchSPLot, setSearchSPLot] = useState("");
    const { showSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(false);
    const [openAddMat, setOpenAddMat] = useState(false);
    const [drillDownCurrRow, setDrillDownCurrRow] = useState(false);
    const [refreshSPLotGrid, setRefreshSPLotGrid] = useState(false);
    const [openReportNoSeedPopup, setOpenReportNoSeedPopup] = useState(false);
    const spLotViewRef = useRef<SPLotViewRef>(null);
    const { crops, sites, splotStatuses, priorities, state, setIsArchived } = useGlobalState();
    const { roleSelected = "", isArchived } = state;
    const isSiteReady = !!state?.siteSelected;

    // Honour ?isArchived= query param on navigation
    useEffect(() => {
        const isArchivedParam = searchParams.get('isArchived');
        if (isArchivedParam === 'true') {
            setIsArchived(true);
            setMode('view');
        }
    }, [searchParams, setIsArchived]);

    const SPLOT_ACCESSIBLE_FILTERS = ['Processing', 'Completed', 'Created', 'High priority MatIDs'];
    const {
        filterValues, selectedFilter, activeFilter, setFilterValues, setSelectedFilter, setActiveFilter,
    } = usePersistedFilters({
        moduleName: 'splot',
        defaultFilter: 'Processing',
        validFilters: SPLOT_ACCESSIBLE_FILTERS,
        isArchived,
        persistState: false,
    });
    const hasInitializedSplotAccessibleFilter = useRef(false);
    const skipNextFilterPanelRefreshRef = useRef(false);
    const [listValues, setListValue] = useState<ListValueSpLot>({
        crops: [],
        sites: [],
        statuses: [],
        priority: []
    });
    const [accessiblefilters] = useState<string[]>(SPLOT_ACCESSIBLE_FILTERS);

    // On initial load of SP Lot dashboard, force the default accessible filter
    // to Processing (In Progress) so chips + grid data stay aligned.
    useEffect(() => {
        if (hasInitializedSplotAccessibleFilter.current) return;
        if (isArchived || !isSiteReady || activeTab !== 1) return;

        hasInitializedSplotAccessibleFilter.current = true;

        // Only reset to default if there's no persisted active filter for the current tab
        const savedActiveFilter = localStorage.getItem(`splot_${selectedFilter}_activeFilter`);
        if (!savedActiveFilter) {
            if (selectedFilter !== 'Processing') {
                setSelectedFilter('Processing');
            }
            setActiveFilter(null, 'Processing');
        }
        setRefreshSPLotGrid(true);
    }, [
        isArchived,
        isSiteReady,
        activeTab,
        selectedFilter,
        setSelectedFilter,
        setActiveFilter,
    ]);

    // --- Label templates (single fetch, shared by SPLotMainGrid + CreateLabelDialog) ---
    const { data: allLabelTemplates } = useGetLabelTemplates({
        query: {
            queryKey: ['label-templates'],
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            select: (res: any) => (res?.data ?? []) as LabelTemplate[],
        },
    });

    const splotTemplates = useMemo(() =>
        ((allLabelTemplates as LabelTemplate[]) ?? []).filter((t) => t.template_type?.toLowerCase() === 'splot'),
        [allLabelTemplates]
    );
    const materialTemplates = useMemo(() =>
        ((allLabelTemplates as LabelTemplate[]) ?? []).filter((t) => t.template_type?.toLowerCase() === 'material'),
        [allLabelTemplates]
    );

    const setSplotGridDataSafe = useCallback((rows: any) => {
        const nextRows = Array.isArray(rows) ? rows : [];
        const isSplotPayload = nextRows.length === 0 || nextRows.some((row: any) => row && typeof row === 'object' && 'splot_id' in row);
        if (!isSplotPayload) return;
        setSpLotGridData(nextRows as any);
    }, []);

    const { mutate: searchBoxSPLot } = useSearchBoxSPLot({
        mutation: {
            onSuccess: (data: any) => {
                setSplotGridDataSafe(data?.data ?? []);
                setIsLoading(false);
            },
            onError: (error: any) => {
                setSplotGridDataSafe([]);
                let errorMessage = (error?.response?.data?.detail) || "Error";
                showSnackbar({ message: errorMessage, severity: 'error' });
                setIsLoading(false);
            }
        },
    })

    const handleSetSplotFilterValues = useCallback((data: any[]) => {
        if (isGlobalSearchActive) return;
        setFilterValues(data);
    }, [isGlobalSearchActive, setFilterValues]);

    const resolveAccessibleSplotFilter = useCallback((statusIds: any): string => {
        return resolveAccessibleFilterByStatusIds({
            statusIds,
            filters: SPLOT_ACCESSIBLE_FILTERS,
            excludedFilters: ['High priority MatIDs'],
            getIdsForFilter: (filter) => getSplotStatusIdsByTab(filter, splotStatuses),
        });
    }, [splotStatuses, SPLOT_ACCESSIBLE_FILTERS]);

    const hasMeaningfulFilterValue = useCallback((value: any): boolean => {
        if (value === null || value === undefined) return false;
        if (typeof value === 'string') return value.trim().length > 0;
        if (typeof value === 'number') return value !== 0;
        if (typeof value === 'boolean') return value;
        if (Array.isArray(value)) return value.some(hasMeaningfulFilterValue);
        if (typeof value === 'object') return Object.values(value).some(hasMeaningfulFilterValue);
        return false;
    }, []);

    const handleSplotSearch = useCallback(() => {
        const trimmedSearch = searchSPLot?.trim();
        // If search field is cleared, restore previous records
        if (!trimmedSearch) {
            setIsGlobalSearchActive(false);
            setFilterValues([]);
            setSelectedFilter('Processing');
            setActiveFilter(null, 'Processing');
            setRefreshSPLotGrid(true);
            return;
        }

        // Global search should ignore filters and accessible filter tabs.
        const activeFilterTab = selectedFilter || 'Processing';
        setIsGlobalSearchActive(true);
        setFilterValues([]);
        setActiveFilter(null, activeFilterTab);
        setSelectedFilter('');

        setIsLoading(true);
        // Always use global search API regardless of filter state.
        searchBoxSPLot({ splot_id: trimmedSearch, is_archive: isArchived });
    }, [searchSPLot, searchBoxSPLot, isArchived, setFilterValues, setActiveFilter, setSelectedFilter, selectedFilter]);

    useEffect(() => {

        const newArray = sites.map(item => ({
            ...item,
            site_name: [item?.site_name, item?.full_code].filter(Boolean).join('-')
        }));

        const statusNewArray = splotStatuses.map(item => ({
            ...item,
            status_value: toTitleCase(item?.status_value)
        }));

        setListValue((prev) => ({
            ...prev, "crops": crops ?? [],
            "sites": newArray,
            "statuses": statusNewArray,
            "priority": priorities
        }));


    }, [crops, sites, splotStatuses, priorities]);

    const onAccessibleFilterChange = useCallback((filter: string) => {
        if (selectedFilter === filter) return;

        skipNextFilterPanelRefreshRef.current = true;
        setIsGlobalSearchActive(false);
        setActiveFilter(null, filter);
        setSelectedFilter(filter);
        setRefreshSPLotGrid(true);
    }, [selectedFilter, setSelectedFilter, setActiveFilter]);

    const handleFilterApplied = useCallback((filterData: any | null) => {
        setIsGlobalSearchActive(false);
        const hasFilterData = hasMeaningfulFilterValue(filterData);
        setActiveFilter(hasFilterData ? filterData : null);
        const mappedFilter = resolveAccessibleSplotFilter(filterData?.status_id);
        if (selectedFilter !== mappedFilter) {
            setSelectedFilter(mappedFilter);
        }
    }, [setActiveFilter, resolveAccessibleSplotFilter, selectedFilter, setSelectedFilter, hasMeaningfulFilterValue]);

    useEffect(() => {
        localStorage.setItem('spLotActiveTab', activeTab.toString());
    }, [activeTab]);

    // When site changes, return from drilldown (if any) and force SP Lot grid refresh.
    const prevSplotSiteRef = useRef(state?.siteSelected);
    useEffect(() => {
        const prevSite = prevSplotSiteRef.current;
        prevSplotSiteRef.current = state?.siteSelected;
        if (!prevSite || prevSite === state?.siteSelected) return;

        setSearchSPLot('');
        setSplotGridDataSafe([]);
        setRefreshSPLotGrid(true);

        if (mode === 'drill') {
            setMode('view');
            setDrillDownParam(null);
        }
    }, [state?.siteSelected, mode, setSplotGridDataSafe]);

    return (
        <div data-testid="SPLotPage" style={{ padding: '0 16px' }}>
            <Grid2 container sx={{ px: 1, pt: 1, pb: 0 }}>
                <SPLotTab
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    rightAction={undefined}
                    isArchived={isArchived}
                    onBackToCurrentView={async () => {
                        if (!isArchived) {
                            // Switching TO archived — confirm if in drill mode
                            if (activeTab === 1 && mode === 'drill' && spLotViewRef.current) {
                                const confirmed = await spLotViewRef.current.closingConfirmation();
                                if (!confirmed) return;
                            }
                            navigate(`/splot?isArchived=true&activeTab=${activeTab}`);
                        } else {
                            // Switching BACK from archived — navigate to current view
                            setIsArchived(false);
                            navigate(`/splot?activeTab=${activeTab}`);
                        }
                    }}
                />
                {isSiteReady && <DashboardHeader
                    title="SP Lot Dashboard"
                    accessibleFilters={accessiblefilters}
                    selectedFilter={selectedFilter}
                    onFilterChange={onAccessibleFilterChange}
                    isFilterVisible={isFilterVisible}
                    setFilterVisible={setFilterVisible}
                    showFilters={!isArchived}
                    setFilterFieldData={handleSetSplotFilterValues}
                    filterValues={isGlobalSearchActive ? [] : filterValues}
                    isDashboardHeaderVisiBle={activeTab == 1 && mode == "view"}
                    filterPanelProps={{
                        moduleName: 'splot',
                        listValues: listValues,
                        setGridData: setSplotGridDataSafe,
                        showArchived: isArchived,
                        persistState: false,
                        onRefreshData: () => {
                            if (skipNextFilterPanelRefreshRef.current) {
                                skipNextFilterPanelRefreshRef.current = false;
                                return;
                            }
                            if (isGlobalSearchActive) return;
                            setRefreshSPLotGrid(true);
                        },
                        accessibleFilterTab: selectedFilter,
                        onFilterApplied: handleFilterApplied,

                    }}
                    searchSlot={
                        <>
                            <TextField placeholder="Enter Trial, Material, Pollination, Container, or SP Lot ID" autoFocus variant="outlined" value={searchSPLot}
                                onChange={(e) => {
                                    const nextValue = e.target.value;
                                    setSearchSPLot(nextValue);
                                }}
                                sx={(theme) => ({
                                    ...MtComStyleObj(theme).textFocusHoverStyle, ...MtComStyleObj(theme).textBoxValueStyle,
                                    backgroundColor: '#FFFFFF',
                                    minWidth: { xs: 200, sm: 300, md: 420, lg: 480 },
                                })}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSplotSearch();
                                }}
                                slotProps={{
                                    input: {
                                        sx: { height: 40 },
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }
                                }} />
                            <Button variant="contained" disableRipple sx={(theme) => ({
                                ...MtComStyleObj(theme).disabledGreenBtnStyle,
                                padding: "10px"
                            })} className={classes.greenButton} onClick={handleSplotSearch} loading={isLoading} loadingPosition="start">Search</Button>
                        </>
                    }
                    actionButtonsSlot={
                        !isArchived ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    disableRipple
                                    color="primary"
                                    className={classes.whiteButton}
                                    onClick={() => setOpenReportNoSeedPopup(true)}
                                >
                                    <Typography className={classes.createMaterialLabelText}>Report No Seed</Typography>
                                </Button>

                                <Button
                                variant="outlined"
                                disableRipple
                                color="primary"
                                className={classes.whiteButton}
                                onClick={() => setShowMaterialLabelPopup(true)}
                                startIcon={
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Create Material Label">
                                        <g clipPath="url(#clip0_6754_30730)">
                                            <path d="M4.00065 5.99998V1.33331H12.0007V5.99998M4.00065 12H2.66732C2.3137 12 1.97456 11.8595 1.72451 11.6095C1.47446 11.3594 1.33398 11.0203 1.33398 10.6666V7.33331C1.33398 6.97969 1.47446 6.64055 1.72451 6.3905C1.97456 6.14046 2.3137 5.99998 2.66732 5.99998H13.334C13.6876 5.99998 14.0267 6.14046 14.2768 6.3905C14.5268 6.64055 14.6673 6.97969 14.6673 7.33331V10.6666C14.6673 11.0203 14.5268 11.3594 14.2768 11.6095C14.0267 11.8595 13.6876 12 13.334 12H12.0007M4.00065 9.33331H12.0007V14.6666H4.00065V9.33331Z" stroke="#1E1E1E" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_6754_30730">
                                                <rect width="16" height="16" fill="white" />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                }
                            >
                              <Typography className={classes.createMaterialLabelText}>Create Material Label</Typography>
                            </Button>

                            {["S2S-Lead", "Technician", "Admin"].includes(roleSelected) &&
                                <Button
                                    startIcon={<AddIcon />}
                                    disableRipple
                                    className={classes.greenButton}
                                    onClick={() => setOpenSPLotDialog(true)}
                                    sx={(theme) => ({
                                        width: 150, padding: "10px 20px", ...MtComStyleObj(theme).disabledGreenBtnStyle
                                    })}
                                >
                                    Create SP Lot
                                </Button>}
                        </Box>
                        ) : undefined
                    }
                    kpiToggleButton={(activeTab === 1 || activeTab === 2) && !isArchived && !showKPI ? (
                        <Button
                            onClick={() => setShowKPI((prev) => !prev)}
                            variant="outlined"
                            disableRipple
                            startIcon={
                                <Box component="img" src={barchartIcon} alt="Bar chart" sx={{ width: 16, height: 16 }} />
                            }
                            sx={{
                                minWidth: 'fit-content',
                                height: 27,
                                borderRadius: '8px',
                                paddingLeft: '12px',
                                paddingRight: '12px',
                                gap: '8px',
                                opacity: 1,
                                textTransform: 'none',
                                background: '#FFFFFF',
                                border: '1px solid #404040',
                                borderTop: '1px solid #404040',
                                color: '#0A0A0A',
                                '&:hover': {
                                    background: '#FFFFFF',
                                    border: '1px solid #404040',
                                    borderTop: '1px solid #404040',
                                },
                            }}
                        >
                            Show KPIs
                        </Button>
                    ) : undefined}
                    kpiContent={activeTab === 1 && mode === 'view' && !isArchived ? (
                        <Collapse
                            in={showKPI}
                            timeout={{ enter: 420, exit: 260 }}
                            easing={{ enter: 'cubic-bezier(0.22, 1, 0.36, 1)', exit: 'cubic-bezier(0.4, 0, 1, 1)' }}
                            sx={{
                                width: '100%',
                                overflow: 'hidden',
                                '& .MuiCollapse-wrapperInner': {
                                    opacity: showKPI ? 1 : 0,
                                    transform: showKPI ? 'translateY(0px)' : 'translateY(-8px)',
                                    transition: 'opacity 320ms ease, transform 320ms ease',
                                },
                            }}
                        >
                            <KPI
                                kpiSection='splot'
                                hideButton={
                                    <Button
                                        onClick={() => setShowKPI((prev) => !prev)}
                                        variant="outlined"
                                        disableRipple
                                        startIcon={<VisibilityOffOutlinedIcon fontSize="small" />}
                                        sx={{
                                            minWidth: 'fit-content',
                                            height: 27,
                                            borderRadius: '8px',
                                            paddingLeft: '12px',
                                            paddingRight: '12px',
                                            gap: '8px',
                                            opacity: 1,
                                            textTransform: 'none',
                                            background: '#FFFFFF',
                                            border: '1px solid #404040',
                                            borderTop: '1px solid #404040',
                                            color: '#0A0A0A',
                                            '&:hover': {
                                                background: '#FFFFFF',
                                                border: '1px solid #404040',
                                                borderTop: '1px solid #404040',
                                            },
                                        }}
                                    >
                                        Hide KPIs
                                    </Button>
                                }
                            />
                        </Collapse>
                    ) : undefined}
                />}
                {!isSiteReady && (
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 6 }}>
                        <Typography className={classes.labelNoto16Bold600}>Loading site context...</Typography>
                    </Box>
                )}
                {isSiteReady && <SPLotDataGrid
                    hidden={activeTab != 1 || mode !== "view"}
                    setMode={setMode}
                    setDrillDownParam={setDrillDownParam}
                    gridData={checkArray(spLotGridData)}
                    refreshSPLotGrid={refreshSPLotGrid}
                    setRefreshSPLotGrid={setRefreshSPLotGrid}
                    showArchivedSPLots={isArchived}
                    selectedFilter={selectedFilter}
                    splotTemplates={splotTemplates}
                    activeFilter={activeFilter}
                />}

                {isSiteReady && <TrailDataGrid
                    hidden={activeTab !== 2}
                    showArchivedTrials={isArchived}
                    showKPI={showKPI && !isArchived}
                    activeTab={activeTab}
                />}

                {isSiteReady && (mode === "drill" && activeTab == 1) && <Box sx={{ mt: 2, width: "100%" }}><SPLotView ref={spLotViewRef} setMode={setMode} drillDownParam={drillDownData} onClose={() => {
                    if (!isGlobalSearchActive) {
                        setRefreshSPLotGrid(true);
                    }
                }} showArchivedSPLots={isArchived} /></Box>}
            </Grid2>
            <SPLotFormDialog
                open={openSPLotDialog}
                setOpenSPLotDialog={setOpenSPLotDialog}
                setOpenAddMat={setOpenAddMat}
                setDrillDownCurrRow={setDrillDownCurrRow}
                setRefreshSPLotGrid={setRefreshSPLotGrid}
                splotTemplates={splotTemplates}
            />
            <AddEditSPLotFormDialog openAddEditMatModal={openAddMat} setaddEditMatModal={setOpenAddMat} mode={"Add"} drillDownCurrRow={drillDownCurrRow} />
            <CreateLabelDialog open={showMaterialLabelPopup} drillDownCurrRow={null} setCreateLableModal={setShowMaterialLabelPopup} showRegisterToMint materialTemplates={materialTemplates} />
            <ReportNoSeedPopup
                open={openReportNoSeedPopup}
                onClose={() => setOpenReportNoSeedPopup(false)}
                onSubmit={(payload) => {
                    showSnackbar({
                        message: `No Seed reported for ${payload.matid}.`,
                        severity: 'success'
                    });
                }}
            />
        </div >
    )
}
export default SPLotPage;
